export type Experience = 'legacy' | 'new'

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

export function decodeJwtPayload(smartAuth: string): Record<string, unknown> | null {
  const parts = smartAuth.split('.')
  if (parts.length < 2) return null
  const b64 = parts[1].replaceAll('-', '+').replaceAll('_', '/')
  const padLength = (4 - (b64.length % 4)) % 4
  const padded = b64 + '='.repeat(padLength)
  try {
    const parsed: unknown = JSON.parse(atob(padded))
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

export type Dev2222Mode = 'jwt' | 'forceA' | 'forceB'

export function readFeature2222Claim(
  payload: Record<string, unknown> | null,
): string | undefined {
  if (!payload) return undefined
  const nested = payload.features
  if (isRecord(nested)) {
    const v = nested['2222']
    if (typeof v === 'string') return v
  }
  const flat = payload['2222']
  if (typeof flat === 'string') return flat
  return undefined
}

function normalizeAb(raw: string): 'a' | 'b' | undefined {
  const c = raw.trim().charAt(0).toLowerCase()
  if (c === 'a') return 'a'
  if (c === 'b') return 'b'
  return undefined
}

export function resolveExperience(
  payload: Record<string, unknown> | null,
  whenMissing: Experience,
): Experience {
  if (!payload) return whenMissing
  const raw = readFeature2222Claim(payload)
  if (!raw) return whenMissing
  const ab = normalizeAb(raw)
  if (ab === 'a') return 'legacy'
  if (ab === 'b') return 'new'
  return whenMissing
}

export function resolveExperienceWithDevMode(
  payload: Record<string, unknown> | null,
  whenMissing: Experience,
  mode: Dev2222Mode,
): Experience {
  if (mode === 'forceA') return 'legacy'
  if (mode === 'forceB') return 'new'
  return resolveExperience(payload, whenMissing)
}

export function resolveShellExperience(
  jwtPayload: Record<string, unknown> | null,
  pathExperience: Experience | undefined,
  dev2222Mode: Dev2222Mode,
  whenMissing: Experience,
): Experience {
  if (pathExperience) return pathExperience
  const effectiveMode = import.meta.env.DEV ? dev2222Mode : 'jwt'
  return resolveExperienceWithDevMode(jwtPayload, whenMissing, effectiveMode)
}
